import {
    ATTRIBUTION_TAB_IDS,
    CAMPAIGN_OBJECTIVES,
    CLICK_IMPRESSION_REPORTING_METHODS,
    DATA_SCOPES,
    INITIAL_FORM_DATA,
    PLATFORMS,
    PLATFORM_TEMPLATES,
    PLATFORM_TEMPLATE_DISALLOWED_EVENT_TYPES,
    PLATFORM_TEMPLATE_DISALLOWED_MACROS_BY_EVENT_TYPE,
    POSTBACK_EVENT_TYPES,
    REQUEST_TYPES,
    substitutePostbackNetworkName,
} from './consts';
import { applyPostbackEventsPlatformsNext } from './postbackEventsPlatformsUpdates';
import { POSTBACK_TEMPLATE_KEYS_BY_TEMPLATE, entryAppliesToCell } from './templateEntries';

const deepFreeze = obj => {
    if (obj === null || typeof obj !== 'object' || Object.isFrozen(obj)) return obj;
    Object.values(obj).forEach(deepFreeze);
    return Object.freeze(obj);
};

const COMMON_AFFILIATE_GENERAL = {
    platforms: [PLATFORMS.IOS, PLATFORMS.ANDROID, PLATFORMS.WEB],
    campaign_objectives: [CAMPAIGN_OBJECTIVES.INSTALLS, CAMPAIGN_OBJECTIVES.IN_APP_EVENTS],
    receive_postbacks: true,
    installs_data_scope: DATA_SCOPES.ATTRIBUTED_ONLY,
    events_data_scope: DATA_SCOPES.ATTRIBUTED_ONLY,
};

const COMMON_TRACKING_LINK_OPTIONS = {
    click_impression_reporting_method: CLICK_IMPRESSION_REPORTING_METHODS.CLIENT_SIDE,
};

// singular_key → partner_macro string. Keys absent here leave the row's partner_macro empty.
const HASOFFERS_PARTNER_MACROS = {
    p: '{device_os}',
    ip: '{ip}',
    cl: '{aff_click_id}',
    ve: '{device_os_version}',
    idfa: '{ios_ifa}',
    aifa: '{google_aid}',
    pcn: '{offer_name}',
    pcid: '{offer_id}',
    pcrn: '{file_name}',
    pcrid: '{file_id}',
    psn: '{affiliate_name}',
    psid: '{affiliate_id}',
    paffn: '{affiliate_name}',
    paffid: '{affiliate_id}',
};

const AFFISE_PARTNER_MACROS = {
    ip: '{ip}',
    cl: '{clickid}',
    ve: '{os_version}',
    pcn: '{offer_name}',
    pcid: '{offer_id}',
    psn: '{affiliate_name}',
    psid: '{pid}',
    paffn: '{affiliate_name}',
    paffid: '{pid}',
};

const EVERFLOW_PARTNER_MACROS = {
    p: '{platform}',
    ip: '{user_ip}',
    cl: '{transaction_id}',
    idfa: '{idfa}',
    aifa: '{google_aid}',
    pcn: '{offer_name}',
    pcid: '{offer_id}',
    pcrn: '{creative_name}',
    pcrid: '{creative_id}',
    psn: '{affiliate_name}',
    psid: '{affiliate_id}',
    paffn: '{affiliate_name}',
    paffid: '{affiliate_id}',
};

// Per-template postback URL pattern. The `{{domain}}` placeholder is the only user-editable
// substring; everything else is fixed by the affiliate platform. Submit validation enforces
// that the saved URL conforms to the same pattern.
export const PLATFORM_TEMPLATE_POSTBACK_URL_PATTERNS = {
    [PLATFORM_TEMPLATES.HASOFFERS]: 'https://{{domain}}.go2cloud.org/aff_goal',
    [PLATFORM_TEMPLATES.AFFISE]: 'https://offers-{{domain}}.affise.com/postback',
    [PLATFORM_TEMPLATES.EVERFLOW]: 'https://www.{{domain}}.com',
};

// Per-(template, event_type) URL pattern overrides. When a cell has an entry here it wins
// over the base template pattern. Fixed (non-user-mutable) query params are baked into the
// pattern itself so they're auto-included when the URL is assembled.
const PLATFORM_TEMPLATE_POSTBACK_URL_PATTERNS_BY_EVENT_TYPE = {
    [PLATFORM_TEMPLATES.HASOFFERS]: {
        [POSTBACK_EVENT_TYPES.CONVERSIONS]: 'https://{{domain}}.go2cloud.org/aff_lsr',
        [POSTBACK_EVENT_TYPES.POST_INSTALL_EVENTS]: 'https://{{domain}}.go2cloud.org/aff_goal?a=lsr',
        [POSTBACK_EVENT_TYPES.REVENUE_EVENTS]: 'https://{{domain}}.go2cloud.org/aff_goal?a=lsr',
    },
    [PLATFORM_TEMPLATES.AFFISE]: {
        [POSTBACK_EVENT_TYPES.CONVERSIONS]: 'https://offers-{{domain}}.affise.com/postback?goal=install',
    },
};

export const getPostbackUrlPattern = (template, eventType) =>
    PLATFORM_TEMPLATE_POSTBACK_URL_PATTERNS_BY_EVENT_TYPE[template]?.[eventType] ||
    PLATFORM_TEMPLATE_POSTBACK_URL_PATTERNS[template] ||
    null;

const POSTBACK_URL_DOMAIN_TOKEN = '{{domain}}';

export const POSTBACK_URL_DOMAIN_PLACEHOLDER = 'your-domain';

// The domain piece must be non-empty and free of URL separators or whitespace. The BE submit
// validator enforces the same shape via `_POSTBACK_URL_DOMAIN_REGEX`.
export const POSTBACK_URL_DOMAIN_REGEX = /^[^/?#\s]+$/;

// Single source of truth for the query params each affiliate template appends to
// `postback_url_base`. Each template has at most one optional security token (rendered as an
// optional input) and at most one required param (rendered as a required input). The
// `*_KEYS` exports + the strip list below are derived from this map.
const TEMPLATE_QUERY_PARAMS = {
    [PLATFORM_TEMPLATES.HASOFFERS]: { securityTokenKey: 'security_token' },
    [PLATFORM_TEMPLATES.AFFISE]: { securityTokenKey: 'secure' },
    [PLATFORM_TEMPLATES.EVERFLOW]: { securityTokenKey: 'verification_token', requiredKey: 'nid' },
};

const pickTemplateKey = prop =>
    Object.fromEntries(
        Object.entries(TEMPLATE_QUERY_PARAMS)
            .filter(([, value]) => !!value[prop])
            .map(([template, value]) => [template, value[prop]])
    );

export const PLATFORM_TEMPLATE_SECURITY_TOKEN_KEYS = pickTemplateKey('securityTokenKey');
export const PLATFORM_TEMPLATE_REQUIRED_QUERY_PARAM_KEYS = pickTemplateKey('requiredKey');

// All query keys a template appends to postback_url_base — used to strip them before matching
// the URL against the bare `prefix + domain + suffix` pattern in extractPostbackUrlDomain.
const TEMPLATE_QUERY_PARAM_KEYS_BY_TEMPLATE = Object.fromEntries(
    Object.entries(TEMPLATE_QUERY_PARAMS).map(([template, { securityTokenKey, requiredKey }]) => [
        template,
        [securityTokenKey, requiredKey].filter(Boolean),
    ])
);

// Allowed characters in a security-token value: alphanumeric + `_-.~`. Empty is allowed
// (the token field is optional; submit validation enforces required-ness when applicable).
export const POSTBACK_URL_SECURITY_TOKEN_REGEX = /^[A-Za-z0-9_\-.~]*$/;

export const buildPostbackUrlForTemplate = (template, domain, eventType) => {
    const pattern = getPostbackUrlPattern(template, eventType);
    return pattern ? pattern.replace(POSTBACK_URL_DOMAIN_TOKEN, domain || '') : null;
};

// Split a URL into [pathPart, queryPart, fragmentPart]. We split manually rather than going
// through `new URL(...)` because the URL constructor rejects affiliate-template intermediate
// states (e.g. an empty domain in `https://.go2cloud.org/aff_goal`), which we still need to
// round-trip through the security-token helpers while the user is editing. The fragment is
// peeled first so `?` inside `#frag?…` does not get treated as the query separator — matches
// the BE's `_strip_query_param_from_url`.
const splitUrlAndQuery = url => {
    const hashIdx = url.indexOf('#');
    const fragment = hashIdx < 0 ? '' : url.slice(hashIdx);
    const head = hashIdx < 0 ? url : url.slice(0, hashIdx);
    const qIdx = head.indexOf('?');
    if (qIdx < 0) return [head, '', fragment];
    return [head.slice(0, qIdx), head.slice(qIdx + 1), fragment];
};

const buildUrlFromParts = (path, params, fragment = '') => {
    const query = params.toString();
    const queryPart = query ? `?${query}` : '';
    return `${path}${queryPart}${fragment}`;
};

// Generic single-key URL query helpers. Higher-level wrappers below specialize them by template.
const stripQueryParamFromUrl = (url, key) => {
    if (!key || typeof url !== 'string' || !url) return url || '';
    const [path, query, fragment] = splitUrlAndQuery(url);
    const params = new URLSearchParams(query);
    if (!params.has(key)) return url;
    params.delete(key);
    return buildUrlFromParts(path, params, fragment);
};

const extractQueryParamFromUrl = (url, key) => {
    if (!key || typeof url !== 'string' || !url) return '';
    const [, query] = splitUrlAndQuery(url);
    return new URLSearchParams(query).get(key) || '';
};

const setQueryParamInUrl = (url, key, value) => {
    if (!key || typeof url !== 'string' || !url) return url || '';
    const [path, query, fragment] = splitUrlAndQuery(url);
    const params = new URLSearchParams(query);
    if (value) {
        params.set(key, value);
    } else {
        params.delete(key);
    }
    return buildUrlFromParts(path, params, fragment);
};

// Strip every template-owned query param (security token + any required ones like Everflow's nid)
// before matching the URL against the bare pattern. Returns the input unchanged for templates
// without a URL pattern.
const stripAllTemplateQueryParamsFromUrl = (template, url) => {
    if (typeof url !== 'string' || !url) return url || '';
    const keys = TEMPLATE_QUERY_PARAM_KEYS_BY_TEMPLATE[template] || [];
    return keys.reduce((acc, key) => stripQueryParamFromUrl(acc, key), url);
};

// Security-token wrappers (per-template optional query key).
export const extractSecurityToken = (template, url) =>
    extractQueryParamFromUrl(url, PLATFORM_TEMPLATE_SECURITY_TOKEN_KEYS[template]);

export const setSecurityTokenInUrl = (template, url, token) =>
    setQueryParamInUrl(url, PLATFORM_TEMPLATE_SECURITY_TOKEN_KEYS[template], token);

// Required-query-param wrappers (currently only Everflow's nid).
export const extractRequiredQueryParamValue = (template, url) =>
    extractQueryParamFromUrl(url, PLATFORM_TEMPLATE_REQUIRED_QUERY_PARAM_KEYS[template]);

export const setRequiredQueryParamInUrl = (template, url, value) =>
    setQueryParamInUrl(url, PLATFORM_TEMPLATE_REQUIRED_QUERY_PARAM_KEYS[template], value);

export const extractPostbackUrlDomain = (template, url, eventType) => {
    const pattern = getPostbackUrlPattern(template, eventType);
    if (!pattern || typeof url !== 'string' || !url) return '';
    // Strip ALL template-owned query params first so the suffix match lines up with the bare pattern.
    // Also peel any trailing fragment — the bare pattern has no `#…`, so we'd otherwise reject
    // legitimate `…/aff_goal#frag` URLs.
    const stripped = stripAllTemplateQueryParamsFromUrl(template, url);
    const hashIdx = stripped.indexOf('#');
    const cleaned = hashIdx < 0 ? stripped : stripped.slice(0, hashIdx);
    const [prefix, suffix] = pattern.split(POSTBACK_URL_DOMAIN_TOKEN);
    if (!cleaned.startsWith(prefix) || !cleaned.endsWith(suffix)) return '';
    return cleaned.slice(prefix.length, cleaned.length - suffix.length);
};

export const resolvePostbackUrl = (template, eventType, overallTab, domainFallback = '') => {
    const baseUrl = overallTab?.postback_url_base || '';
    const perEventUrl = overallTab?.different_domains_per_postback ? overallTab[`${eventType}_postback_url`] : '';
    const pattern = getPostbackUrlPattern(template, eventType);
    if (!pattern) {
        return perEventUrl?.trim() || baseUrl;
    }
    const perEventDomain = extractPostbackUrlDomain(template, perEventUrl, eventType);
    const overallDomain = extractPostbackUrlDomain(template, baseUrl);
    const domain = perEventDomain || overallDomain || domainFallback;
    let url = buildPostbackUrlForTemplate(template, domain, eventType);
    const requiredParam = extractRequiredQueryParamValue(template, baseUrl);
    if (requiredParam) {
        url = setRequiredQueryParamInUrl(template, url, requiredParam);
    }
    const token = extractSecurityToken(template, baseUrl);
    if (token) {
        url = setSecurityTokenInUrl(template, url, token);
    }
    return url;
};

const STANDARD_EVENTS = [
    { event_type: POSTBACK_EVENT_TYPES.CONVERSIONS, platforms: [PLATFORMS.IOS, PLATFORMS.ANDROID] },
    { event_type: POSTBACK_EVENT_TYPES.POST_INSTALL_EVENTS, platforms: [PLATFORMS.IOS, PLATFORMS.ANDROID] },
    { event_type: POSTBACK_EVENT_TYPES.REVENUE_EVENTS, platforms: [PLATFORMS.IOS, PLATFORMS.ANDROID] },
];

export const PLATFORM_TEMPLATE_DEFAULTS = deepFreeze({
    [PLATFORM_TEMPLATES.NONE]: {
        general: {
            platforms: [PLATFORMS.IOS, PLATFORMS.ANDROID],
            campaign_objectives: [CAMPAIGN_OBJECTIVES.INSTALLS, CAMPAIGN_OBJECTIVES.RE_ENGAGEMENTS],
            receive_postbacks: true,
        },
        tracking_link_options: COMMON_TRACKING_LINK_OPTIONS,
        postback_overall_setup: {
            postback_events_and_platforms: [
                { event_type: POSTBACK_EVENT_TYPES.CONVERSIONS, platforms: [PLATFORMS.IOS, PLATFORMS.ANDROID] },
            ],
        },
    },
    [PLATFORM_TEMPLATES.HASOFFERS]: {
        general: COMMON_AFFILIATE_GENERAL,
        tracking_link_options: {
            ...COMMON_TRACKING_LINK_OPTIONS,
            partner_macros: HASOFFERS_PARTNER_MACROS,
        },
        postback_overall_setup: {
            postback_events_and_platforms: STANDARD_EVENTS,
            different_domains_per_postback: false,
            needs_app_level_macro: false,
            enable_event_mapping: false,
        },
        postback_template_keys: POSTBACK_TEMPLATE_KEYS_BY_TEMPLATE[PLATFORM_TEMPLATES.HASOFFERS],
    },
    [PLATFORM_TEMPLATES.AFFISE]: {
        general: COMMON_AFFILIATE_GENERAL,
        tracking_link_options: {
            ...COMMON_TRACKING_LINK_OPTIONS,
            partner_macros: AFFISE_PARTNER_MACROS,
        },
        postback_overall_setup: {
            postback_events_and_platforms: [
                ...STANDARD_EVENTS,
                { event_type: POSTBACK_EVENT_TYPES.FRAUD_POSTBACKS, platforms: [PLATFORMS.IOS, PLATFORMS.ANDROID] },
            ],
            different_domains_per_postback: false,
            needs_app_level_macro: false,
            enable_event_mapping: false,
        },
        postback_template_keys: POSTBACK_TEMPLATE_KEYS_BY_TEMPLATE[PLATFORM_TEMPLATES.AFFISE],
    },
    [PLATFORM_TEMPLATES.EVERFLOW]: {
        general: COMMON_AFFILIATE_GENERAL,
        tracking_link_options: {
            ...COMMON_TRACKING_LINK_OPTIONS,
            partner_macros: EVERFLOW_PARTNER_MACROS,
        },
        postback_overall_setup: {
            postback_events_and_platforms: STANDARD_EVENTS,
            different_domains_per_postback: false,
            needs_app_level_macro: false,
            enable_event_mapping: false,
        },
        postback_template_keys: POSTBACK_TEMPLATE_KEYS_BY_TEMPLATE[PLATFORM_TEMPLATES.EVERFLOW],
    },
});

const { GENERAL, TRACKING_LINK_OPTIONS, POSTBACK_OVERALL_SETUP, POSTBACK_TEMPLATES } = ATTRIBUTION_TAB_IDS;

const ALL_TEMPLATE_KEYS = {
    [GENERAL]: ['platforms', 'campaign_objectives', 'receive_postbacks', 'installs_data_scope', 'events_data_scope'],
    [TRACKING_LINK_OPTIONS]: ['click_impression_reporting_method', 'view_through_campaigns'],
    [POSTBACK_OVERALL_SETUP]: ['different_domains_per_postback', 'needs_app_level_macro', 'enable_event_mapping'],
};

// Overlay one tab object: for every key in `keys`, set to entryDefaults[key] (which may be undefined).
const overlayTab = (prevTab, entryDefaults, keys) => {
    const next = { ...prevTab };
    keys.forEach(key => {
        next[key] = entryDefaults ? entryDefaults[key] : undefined;
    });
    return next;
};

// Set partner_parameter_key on the row matching `singular_macro`, or append a new row if absent.
const upsertPartnerParameterKey = (rows, singularMacro, key) => {
    const idx = rows.findIndex(r => r.singular_macro === singularMacro);
    if (idx < 0) return [...rows, { singular_macro: singularMacro, partner_parameter_key: key }];
    return rows.map((row, i) => (i === idx ? { ...row, partner_parameter_key: key } : row));
};

// Apply all postback_template_keys entries that match this (template, event_type, platform) cell
// to its rows. `entryAppliesToCell` lives in templateEntries.js — same single source of truth
// the macro table UI and FE validator consume.
const applyKeysToCell = (rows, templateValue, eventType, platform, entries, networkName) =>
    entries
        .filter(e => entryAppliesToCell(e, eventType, platform, templateValue))
        .reduce(
            (acc, e) => upsertPartnerParameterKey(acc, substitutePostbackNetworkName(e.macro, networkName), e.key),
            rows || []
        );

// Drop postback-template rows whose macro is forbidden in their (template, event_type) cell.
// Pure transform — leaves cells without disallow rules untouched.
const stripDisallowedMacrosFromTemplates = (formData, templateValue) => {
    const byEvent = PLATFORM_TEMPLATE_DISALLOWED_MACROS_BY_EVENT_TYPE[templateValue];
    if (!byEvent) return formData;
    const prevTemplates = formData[POSTBACK_TEMPLATES] || {};
    const nextTemplates = Object.fromEntries(
        Object.entries(prevTemplates).map(([eventType, byPlatform]) => {
            const disallowed = new Set(byEvent[eventType] || []);
            if (!disallowed.size) return [eventType, byPlatform];
            return [
                eventType,
                Object.fromEntries(
                    Object.entries(byPlatform || {}).map(([platform, rows]) => [
                        platform,
                        (rows || []).filter(r => !disallowed.has(r.singular_macro)),
                    ])
                ),
            ];
        })
    );
    return { ...formData, [POSTBACK_TEMPLATES]: nextTemplates };
};

// Drop postback_events_and_platforms entries whose event_type is forbidden by the active template
// — and filter the same event_type out of the cascade target so the cascade removes any
// dependent platform_templates cells.
const stripDisallowedEventTypesFromOverall = (formData, templateValue) => {
    const disallowed = new Set(PLATFORM_TEMPLATE_DISALLOWED_EVENT_TYPES[templateValue] || []);
    if (!disallowed.size) return formData;
    const prevOverall = formData[POSTBACK_OVERALL_SETUP] || {};
    const prevEvents = prevOverall.postback_events_and_platforms || [];
    return {
        ...formData,
        [POSTBACK_OVERALL_SETUP]: {
            ...prevOverall,
            postback_events_and_platforms: prevEvents.filter(e => !disallowed.has(e.event_type)),
        },
    };
};

// Per-row postback-template defaults: for each (event_type, platform) cell already present in
// formData, fill (or add) rows from the template's `postback_template_keys` list. Affiliate
// templates also drop default rows the template doesn't auto-fill — same rationale as the
// tracking-link filter above. The macro-table "Add" dropdown can re-introduce any of them.
const applyPostbackTemplateKeys = (formData, templateValue, entries, networkName) => {
    if (!entries || !entries.length) return formData;
    const prevTemplates = formData[POSTBACK_TEMPLATES] || {};
    const nextTemplates = Object.fromEntries(
        Object.entries(prevTemplates).map(([eventType, byPlatform]) => [
            eventType,
            Object.fromEntries(
                Object.entries(byPlatform || {}).map(([platform, rows]) => [
                    platform,
                    applyKeysToCell(rows, templateValue, eventType, platform, entries, networkName).filter(
                        r => !!(r.partner_parameter_key || '').toString().trim()
                    ),
                ])
            ),
        ])
    );
    return { ...formData, [POSTBACK_TEMPLATES]: nextTemplates };
};

/**
 * Overlay a platform template's defaults on top of `formData`. Pure transform.
 *
 * - Always sets `general.platform_template = templateValue`.
 * - For each scalar/array field the template owns (see ALL_TEMPLATE_KEYS), writes the template's
 *   value, OR `undefined` if the template does not specify it. This is what makes "switch to none
 *   after edits" actually clear the booleans.
 * - For `postback_events_and_platforms`, routes through `applyPostbackEventsPlatformsNext` so the
 *   existing cascade (clearing dependent platform_templates rows on removal, seeding default macro
 *   rows for newly added combos) runs.
 * - Fields outside ALL_TEMPLATE_KEYS (e.g. postback_url_base, app_level_macros, additional_notes)
 *   are left untouched.
 *
 * @param {object} formData current formData
 * @param {'none'|'hasoffers'|'affise'|'everflow'} templateValue platform template selected by the user
 * @param {?string} postbackMacroNetworkName partner postback macro network name (passed to cascade)
 * @returns {object} next formData
 */
export const applyPlatformTemplateDefaults = (formData, templateValue, postbackMacroNetworkName) => {
    const entry = PLATFORM_TEMPLATE_DEFAULTS[templateValue];
    if (!entry) return formData;

    let next = {
        ...formData,
        [GENERAL]: {
            ...overlayTab(formData[GENERAL] || {}, entry[GENERAL], ALL_TEMPLATE_KEYS[GENERAL]),
            platform_template: templateValue,
        },
        [TRACKING_LINK_OPTIONS]: overlayTab(
            formData[TRACKING_LINK_OPTIONS] || {},
            entry[TRACKING_LINK_OPTIONS],
            ALL_TEMPLATE_KEYS[TRACKING_LINK_OPTIONS]
        ),
        [POSTBACK_OVERALL_SETUP]: overlayTab(
            formData[POSTBACK_OVERALL_SETUP] || {},
            entry[POSTBACK_OVERALL_SETUP],
            ALL_TEMPLATE_KEYS[POSTBACK_OVERALL_SETUP]
        ),
    };

    // Merge per-row partner_macro defaults onto existing tracking_link_parameters rows, then
    // drop default rows the affiliate template doesn't auto-fill — the empty-row noise was the
    // top complaint on the pre-template state. We keep:
    //   - rows the template auto-fills (partner_macro non-empty after the map applies)
    //   - rows the user typed a partner_macro into themselves (preserved across template switches)
    //   - custom rows (is_custom === true) — empty or not, they're the user's, not defaults
    // Users can re-add a standard primary row via the table's "Add" dropdown.
    const partnerMacrosMap = entry[TRACKING_LINK_OPTIONS]?.partner_macros;
    if (partnerMacrosMap) {
        const prevRows = next[TRACKING_LINK_OPTIONS].tracking_link_parameters || [];
        next[TRACKING_LINK_OPTIONS] = {
            ...next[TRACKING_LINK_OPTIONS],
            tracking_link_parameters: prevRows
                .map(row =>
                    partnerMacrosMap[row.singular_key] !== undefined
                        ? { ...row, partner_macro: partnerMacrosMap[row.singular_key] }
                        : row
                )
                .filter(row => !!(row.partner_macro || '').toString().trim() || row.is_custom === true),
        };
    }

    // overlayTab above runs first, so the cascade reads the already-overlaid POSTBACK_OVERALL_SETUP
    // (not the caller's original values). This is what makes "switch from a template that set
    // enable_event_mapping=true to a template that doesn't define it" actually clear the flag.
    // Clone the frozen targetEvents so the cascade never holds a reference to the deep-frozen map.
    // Also strip disallowed event types from the target so the cascade drops them (and any
    // dependent platform_templates cells) when the new template forbids them.
    const disallowedEventTypes = new Set(PLATFORM_TEMPLATE_DISALLOWED_EVENT_TYPES[templateValue] || []);
    const frozenEvents = entry[POSTBACK_OVERALL_SETUP]?.postback_events_and_platforms || [];
    const targetEvents = frozenEvents
        .filter(row => !disallowedEventTypes.has(row.event_type))
        .map(row => ({ ...row, platforms: [...row.platforms] }));
    next = applyPostbackEventsPlatformsNext(next, targetEvents, postbackMacroNetworkName);

    // The cascade above only honors what's in `targetEvents` for ADDING entries; entries already
    // in formData but not in target may still linger as deselected. Belt-and-suspenders: also
    // explicitly drop disallowed entries from `postback_events_and_platforms`.
    next = stripDisallowedEventTypesFromOverall(next, templateValue);

    next = applyPostbackTemplateKeys(next, templateValue, entry.postback_template_keys, postbackMacroNetworkName);

    // Finally, drop any pre-existing platform-template rows whose macro the new template forbids
    // Done after the keys cascade so we don't accidentally re-introduce them via `upsertPartnerParameterKey`.
    next = stripDisallowedMacrosFromTemplates(next, templateValue);

    return next;
};

/**
 * Build a fresh `INITIAL_FORM_DATA` for an attribution request, then overlay the chosen template's
 * defaults. Used on the "Yes, reset" path of the change-template confirmation dialog. Ignores any
 * previous formData entirely.
 *
 * @param {'none'|'hasoffers'|'affise'|'everflow'} templateValue platform template selected by the user
 * @param {?string} postbackMacroNetworkName partner postback macro network name
 * @returns {object} fresh formData
 */
export const resetFormDataToTemplate = (templateValue, postbackMacroNetworkName) => {
    const initial = INITIAL_FORM_DATA[REQUEST_TYPES.ATTRIBUTION]({
        postbackMacroNetworkName,
        isExistingRequest: false,
    });
    return applyPlatformTemplateDefaults(initial, templateValue, postbackMacroNetworkName);
};
